import {
    getFacebookSourceIdentity,
    isFacebookCustomType,
} from "helpers/leads/facebookSourceField";

export const validateImportJson = (data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return "Invalid mapping file.";
    }
    if (data.version == null) {
        return "Mapping file is missing a version.";
    }
    if (!Array.isArray(data.mappings)) {
        return "Mapping file is missing a mappings array.";
    }
    const invalidRow = data.mappings.find(
        (row) => !row?.source_key || !row?.destination_key,
    );
    if (invalidRow) {
        return "Each mapping needs a source_key and destination_key.";
    }
    return null;
};

const getFnName = (func) =>
    typeof func === "string" ? func : func?.fn_name;

const getFnValues = (func) => {
    const values = func?.fn_values;
    if (!values || Array.isArray(values)) {
        return {};
    }
    return values;
};

const normalizeComparedFields = (comparedFields) => {
    if (!comparedFields) {
        return {};
    }
    if (Array.isArray(comparedFields)) {
        return comparedFields.reduce((merged, item) => {
            if (!item || typeof item !== "object" || Array.isArray(item)) {
                return merged;
            }
            return { ...merged, ...item };
        }, {});
    }
    if (typeof comparedFields === "object") {
        return comparedFields;
    }
    return {};
};

const getComparedRowsForSource = (comparedFieldsMap, ...keys) => {
    for (const key of keys) {
        if (key == null || key === "") {
            continue;
        }
        const rows = comparedFieldsMap[key] ?? comparedFieldsMap[String(key)];
        if (Array.isArray(rows) && rows.length > 0) {
            return rows;
        }
    }
    return null;
};

/**
 * Resolve the imported row to one of the current source field options.
 *
 * Facebook: `source_type` is the Meta enum. Non-CUSTOM rows (PHONE, EMAIL,
 * CREATED_UNIX_TIME, ...) match on `type`; CUSTOM rows (advertiser
 * questions) match on the question key in `source_key`.
 * Other sources: match on `key`.
 */
const resolveImportedSourceField = ({
    item,
    sourceKey,
    sourceFieldsOptions,
    isFacebookSource,
}) => {
    const options = sourceFieldsOptions || [];
    const sourceType = item.source_type?.toString() ?? "";
    if (
        isFacebookSource &&
        sourceType !== "" &&
        !isFacebookCustomType(sourceType)
    ) {
        const byType = options.find(
            (field) =>
                !field?.isDynamic && field?.type?.toString() === sourceType,
        );
        if (byType) {
            return byType;
        }
    }
    return options.find((field) => field?.key?.toString() === sourceKey);
};

export const toDynamicRowFields = (dynamicField) => {
    if (dynamicField?.type === "advanced_mode") {
        return {
            dynamicMode: "advanced",
            advancedPayload: dynamicField?.payload ?? "",
            fields: [],
        };
    }
    return {
        dynamicMode: "basic",
        fields: (Array.isArray(dynamicField?.payload)
            ? dynamicField.payload
            : []
        ).map((p) => ({
            value: p?.key,
            label: p?.key,
            defaultValue: p?.default ?? "",
            prefix: p?.prefix ?? "",
            suffix: p?.suffix ?? "",
            fn: p?.fn || [],
        })),
    };
};

export const mergeDynamicSourceOptions = (options, names) => {
    const existing = new Set((options || []).map((o) => o?.key?.toString()));
    const missing = [...new Set(names || [])]
        .filter((name) => name && !existing.has(name))
        .map((name) => ({ key: name, label: name, isDynamic: true }));
    return missing.length ? [...(options || []), ...missing] : options;
};

export const applyImportedMappings = ({
    mappings,
    sourceFieldsOptions,
    destinationFields,
    allFunctions,
    compared_fields,
    source_dynamic_fields,
    isFacebookSource = false,
}) => {
    const fieldList = [];
    const dynamicNames = [];
    const dynamicFieldsByName = (source_dynamic_fields || []).reduce(
        (acc, field) => (field?.name ? { ...acc, [field.name]: field } : acc),
        {},
    );
    const functionValues = {
        maxLen: {},
        padWithLength: {},
        padWithCharacter: {},
        left: {},
        right: {},
    };
    const comparedFieldsMap = normalizeComparedFields(compared_fields);

    (mappings || []).forEach((item) => {
        const sourceKey = item.source_key?.toString() ?? "";
        const isStatic = !!item.is_static;
        const isDynamic = !isStatic && !!item.is_dynamic;
        const dynamicName = item.dynamic_field_name || sourceKey;
        const sourceField =
            isStatic || isDynamic
                ? undefined
                : resolveImportedSourceField({
                      item,
                      sourceKey,
                      sourceFieldsOptions,
                      isFacebookSource,
                  });

        if (!sourceField && !isStatic && !isDynamic) {
            return;
        }

        const functions = [];
        (item.fn || []).forEach((func) => {
            const fnName = getFnName(func);
            const funcObj = (allFunctions || []).find(
                (option) => option?.id == fnName,
            );
            if (funcObj?.id) {
                functions.push(funcObj.id);
            }
        });

        const index = fieldList.length;
        (item.fn || []).forEach((func) => {
            const fnName = getFnName(func);
            const fnValues = getFnValues(func);
            if (fnName === "max_len" && fnValues.length != null) {
                functionValues.maxLen[index] = fnValues.length;
            } else if (fnName === "pad_with") {
                if (fnValues.length != null) {
                    functionValues.padWithLength[index] = fnValues.length;
                }
                if (fnValues.character != null) {
                    functionValues.padWithCharacter[index] = fnValues.character;
                }
            } else if (fnName === "left" && fnValues.length != null) {
                functionValues.left[index] = fnValues.length;
            } else if (fnName === "right" && fnValues.length != null) {
                functionValues.right[index] = fnValues.length;
            }
        });

        const destinationKey = item.destination_key;
        const destinationExists = (destinationFields || []).find(
            (field) => field?.type === destinationKey,
        );

        const resolvedSourceKey = isDynamic
            ? dynamicName
            : isStatic
              ? sourceKey
              : (sourceField?.key?.toString?.() ?? sourceKey);
        // UI identity: Facebook rows use the Meta type token unless CUSTOM,
        // in which case the question key. Other sources use the key.
        const sourceIdentity =
            isStatic || isDynamic
                ? resolvedSourceKey
                : isFacebookSource &&
                    sourceField?.type != null &&
                    sourceField.type !== ""
                  ? getFacebookSourceIdentity(sourceField)
                  : resolvedSourceKey;
        const resolvedSourceType =
            isStatic || isDynamic
                ? resolvedSourceKey
                : isFacebookSource
                  ? (sourceField?.type?.toString?.() ?? sourceIdentity)
                  : sourceField?.type;

        if (isDynamic) {
            dynamicNames.push(dynamicName);
        }
        const comparedRows = getComparedRowsForSource(
            comparedFieldsMap,
            sourceIdentity,
            sourceKey,
            resolvedSourceKey,
        );

        fieldList.push({
            source: sourceIdentity,
            sourceType: resolvedSourceType,
            sourceKey: resolvedSourceKey,
            custom: isStatic,
            ...(isDynamic
                ? {
                      name: dynamicName,
                      is_dynamic: true,
                      ...toDynamicRowFields(dynamicFieldsByName[dynamicName]),
                  }
                : {}),
            options: {
                isOpen: false,
                functions,
                default: item.default || "",
                prefix: item.prefix || "",
            },
            compared_fields: comparedRows
                ? { [sourceIdentity]: comparedRows }
                : {},
            destination: destinationExists ? destinationKey : "",
            destinationErrors: true,
            sourceErrors: true,
        });
    });

    return { fieldList, functionValues, dynamicNames };
};

export const extractImportedMappingId = (responseData) => {
    const payload = responseData?.data ?? responseData ?? {};
    if (payload.mapping_id !== undefined && payload.mapping_id !== null) {
        return payload.mapping_id;
    }
    return undefined;
};
